// src/services/cookieService.ts

export interface CookiePreferences {
  necessary: boolean;
  functional: boolean;
  analytics: boolean;
  marketing: boolean;
}

class CookieService {
  private readonly COOKIE_CONSENT_KEY = 'cookie_consent';
  private readonly COOKIE_PREFERENCES_KEY = 'cookie_preferences';
  private readonly COOKIE_EXPIRY = 365;

  setCookie(name: string, value: string, days = 30, path = '/'): void {
    const expires = new Date();
    expires.setTime(expires.getTime() + days * 24 * 60 * 60 * 1000);
    document.cookie = `${name}=${encodeURIComponent(value)}; expires=${expires.toUTCString()}; path=${path}; SameSite=Lax`;
  }

  getCookie(name: string): string | null {
    const cookies = document.cookie.split('; ');
    for (const cookie of cookies) {
      const [key, value] = cookie.split('=');
      if (key === name) {
        return decodeURIComponent(value);
      }
    }
    return null;
  }

  deleteCookie(name: string, path = '/'): void {
    document.cookie = `${name}=; expires=Thu, 01 Jan 1970 00:00:00 UTC; path=${path};`;
  }

  setPreferences(preferences: CookiePreferences): void {
    localStorage.setItem(this.COOKIE_PREFERENCES_KEY, JSON.stringify(preferences));
    this.setCookie(this.COOKIE_CONSENT_KEY, 'true', this.COOKIE_EXPIRY);
  }

  getPreferences(): CookiePreferences | null {
    const stored = localStorage.getItem(this.COOKIE_PREFERENCES_KEY);
    if (stored) {
      try {
        return JSON.parse(stored);
      } catch {
        return null;
      }
    }
    return null;
  }

  hasConsent(): boolean {
    return this.getCookie(this.COOKIE_CONSENT_KEY) === 'true';
  }

  isCategoryAllowed(category: keyof CookiePreferences): boolean {
    if (category === 'necessary') return true;
    const preferences = this.getPreferences();
    if (!preferences) return false;
    return preferences[category] || false;
  }

  getAllCookies(): Record<string, string> {
    const cookies: Record<string, string> = {};
    const cookieList = document.cookie.split('; ');
    for (const cookie of cookieList) {
      const [key, value] = cookie.split('=');
      cookies[key] = decodeURIComponent(value);
    }
    return cookies;
  }

  clearNonNecessaryCookies(): void {
    const cookies = this.getAllCookies();
    const necessaryCookies = ['cookie_consent', 'cookie_preferences', 'client_token', 'admin_token'];
    for (const [name] of Object.entries(cookies)) {
      if (!necessaryCookies.includes(name)) {
        this.deleteCookie(name);
      }
    }
  }

  resetPreferences(): void {
    localStorage.removeItem(this.COOKIE_PREFERENCES_KEY);
    this.deleteCookie(this.COOKIE_CONSENT_KEY);
    this.clearNonNecessaryCookies();
  }
}

export default new CookieService();